import React, { memo, useCallback, useState } from 'react'
import { View, Text } from 'react-native'
import { Image } from 'expo-image'
import { Heart, Zap } from '@/components/ui/icons'
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withSequence,
  useReducedMotion,
} from 'react-native-reanimated'
import { impact, ImpactStyle } from '../../lib/utils/haptics'
import { LinearGradient } from 'expo-linear-gradient'
import { useTranslation } from 'react-i18next'
import { Pressable } from '../ui/Pressable'
import { Rating } from '../ui/Rating'
import { Skeleton } from '../ui/Skeleton'
import { useSemanticColors, RADIUS, SPACING, INTERACTION } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { useFontScale } from '../../lib/hooks/use-font-scale'
import { COLORS, FONTS } from '../../lib/brand'
import { IMAGE_CACHE_POLICY, IMAGE_TRANSITION_MS } from '../../lib/utils/image-cache'
import { toPlaceholder, GENERIC_IMAGE_BLURHASH } from '../../lib/utils/blurhash'
import { prefetchVehicleDetail } from '../../lib/hooks/use-vehicle-detail'
import { usePressAction } from '../../lib/hooks/use-press-action'
import { getVehicleDisplayPriceCents } from '../../lib/utils/pricing'
import { formatPrice } from '../../lib/utils/format'
import type { SearchVehicle } from '../../lib/types/vehicle.types'

interface VehicleCardProps {
  vehicle: SearchVehicle
  isFavorite: boolean
  onPress: () => void
  onToggleFavorite: () => void
  desktop?: boolean
  mobileWidth?: number
}

const MOBILE_WIDTH = 230
const MOBILE_IMAGE_ASPECT_RATIO = MOBILE_WIDTH / 142

function VehicleCardBase({
  vehicle,
  isFavorite,
  onPress,
  onToggleFavorite,
  desktop,
  mobileWidth,
}: VehicleCardProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { enlarged } = useFontScale()
  const { t } = useTranslation()
  const reducedMotion = useReducedMotion()
  // A listing can outlive an external image host or a migrated Storage object.
  // Keep the marketplace grid editorial rather than leaving a dead bitmap when
  // that happens; the same branded placeholder also covers photo-less listings.
  // Store the failed URL (rather than a boolean reset by an effect): FlatList
  // can recycle a card to a new image between renders, and this derives the
  // correct state immediately without one placeholder frame.
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null)
  const imageFailed = vehicle.primaryImageUrl !== null && failedImageUrl === vehicle.primaryImageUrl
  // Heart spring on toggle: scale 1 → 1.32 → 1 over ~280ms with a soft spring.
  // Reduced motion: no scale change. Pure visual delight on every favorite —
  // mirrors Airbnb / Eventbrite's heart pop with cleaner branding.
  const heartScale = useSharedValue(1)
  const heartAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: heartScale.value }],
  }))
  const handleFavoriteToggle = useCallback(() => {
    impact(ImpactStyle.Medium)
    if (!reducedMotion) {
      heartScale.value = withSequence(
        withSpring(1.32, { damping: 8, stiffness: 280, mass: 0.4 }),
        withSpring(1, { damping: 12, stiffness: 220, mass: 0.5 }),
      )
    }
    onToggleFavorite()
  }, [reducedMotion, heartScale, onToggleFavorite])
  // Canonical sticker price — identical across map pin, card, detail,
  // booking intro. Change the formula in `getVehicleDisplayPriceCents`
  // (lib/utils/pricing.ts) to flip the sticker app-wide in one edit.
  const sticker = getVehicleDisplayPriceCents(vehicle)
  const price = formatPrice(sticker.cents)
  const rateLabel = sticker.unit === 'hour' ? t('explore.per_hour') : t('explore.per_day')
  const categoryLabel = vehicle.category ? t(`explore.categories.${vehicle.category}`) : ''
  const isBookable = vehicle.status === 'active'

  const handlePress = usePressAction(() => {
    prefetchVehicleDetail(vehicle.id)
    onPress()
  })

  const resolvedMobileWidth = mobileWidth ?? MOBILE_WIDTH
  const imgH = desktop ? 220 : Math.round(resolvedMobileWidth / MOBILE_IMAGE_ASPECT_RATIO)
  const imageFit = desktop ? 'contain' : 'cover'
  const titleSize = desktop ? 16 : 14
  const priceSize = desktop ? 20 : 16
  const starSize = desktop ? 13 : 12
  const metaSize = desktop ? 13 : 12

  return (
    /*
     * EL CORAZON ES HERMANO DE LA TARJETA, NO SU HIJO.
     *
     * Antes vivia DENTRO del Pressable de la tarjeta y trataba de frenar el
     * toque con `onStartShouldSetResponder` sobre un View. Esa guarda no
     * aguanta en web: medido el 2026-08-25 a 390pt, tocar el corazon NAVEGA a
     * la ficha del carro. Querias guardarlo para despues y perdiste tu lugar en
     * el scroll -- en cada tarjeta, cada vez.
     *
     * Es exactamente la misma guarda que fallo en `BlockUserButton`, donde cada
     * toque dentro de la tarjeta cerraba el dialogo. Ahi la respuesta fue la
     * misma: no anidar. Dos hermanos absolutos no necesitan que nadie arbitre
     * nada, y ademas el corazon puede volver a ser un boton de verdad --con su
     * `accessibilityRole`-- sin producir un <button> dentro de otro <button>.
     */
    <View style={{ width: desktop ? '100%' : resolvedMobileWidth, position: 'relative' }}>
      <Pressable
        onPress={handlePress}
        accessibilityRole="button"
        accessibilityLabel={`${vehicle.makeName} ${vehicle.modelName}, ${price}${rateLabel}`}
        testID="explore.vehicle-card"
        style={{ width: '100%' }}
      >
        {/* Image */}
        <View
          style={{
            height: imgH,
            borderRadius: RADIUS.lg,
            overflow: 'hidden',
            position: 'relative',
            backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#F2F0F6',
          }}
        >
          {vehicle.primaryImageUrl && !imageFailed ? (
            <Image
              source={vehicle.primaryImageUrl}
              // Misma razón que en la lista: la celda se recicla y la imagen
              // anterior no debe sobrevivir un frame. Ver TAGO-MOBILE flicker.
              recyclingKey={vehicle.id}
              // Always a blurhash: listings uploaded before the hash column
              // existed used to fall through to a flat grey box for the whole
              // network round-trip, right on the main marketplace grid.
              placeholder={toPlaceholder(vehicle.primaryImageBlurhash ?? GENERIC_IMAGE_BLURHASH)}
              placeholderContentFit={imageFit}
              style={{ width: '100%', height: '100%' }}
              contentFit={imageFit}
              transition={IMAGE_TRANSITION_MS}
              cachePolicy={IMAGE_CACHE_POLICY}
              onError={() => setFailedImageUrl(vehicle.primaryImageUrl)}
            />
          ) : (
            <LinearGradient
              colors={
                isDark
                  ? ['#1E1A38', '#2A1F4E']
                  : [`${COLORS.purple.DEFAULT}12`, `${COLORS.coral.DEFAULT}10`]
              }
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={{
                width: '100%',
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Text
                style={{
                  fontSize: desktop ? 56 : 40,
                  fontFamily: FONTS.body,
                  color: isDark ? 'rgba(255,255,255,0.06)' : colors.textMuted,
                  opacity: isDark ? 1 : 0.12,
                  letterSpacing: -2,
                }}
              >
                {vehicle.makeName}
              </Text>
            </LinearGradient>
          )}

          {/* Badges */}
          <View
            style={{
              position: 'absolute',
              top: SPACING.sm + 2,
              left: SPACING.sm + 2,
              // The heart is an absolutely-positioned sibling on the opposite
              // corner, so nothing was laying these two out against each other:
              // at 2x system text "Instantáneo" simply ran under it and the card
              // read "Insta<heart>tá". Bounding the cluster short of the heart's
              // 44dp target — and letting it wrap — makes the collision
              // impossible rather than unlikely.
              right: SPACING.sm + 2 + INTERACTION.minTouchTarget + SPACING.xs,
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: SPACING.xs,
            }}
          >
            {categoryLabel ? (
              <View
                style={{
                  backgroundColor: 'rgba(255,255,255,0.92)',
                  paddingHorizontal: SPACING.sm + 1,
                  paddingVertical: SPACING.xs,
                  borderRadius: RADIUS.sm,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    // The pill behind this is rgba(255,255,255,0.92) in BOTH
                    // themes, so its text cannot follow the theme. `textBrand`
                    // resolves to a light lavender in dark mode and measured
                    // 2.43:1 here on ten routes — the darkest brand purple is
                    // what a fixed white pill needs.
                    color: COLORS.purple.DEFAULT,
                    fontFamily: FONTS.body,
                  }}
                >
                  {categoryLabel}
                </Text>
              </View>
            ) : null}
            {!isBookable && (
              <View
                style={{
                  backgroundColor: 'rgba(255,255,255,0.92)',
                  paddingHorizontal: SPACING.sm + 1,
                  paddingVertical: SPACING.xs,
                  borderRadius: RADIUS.sm,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    // Same fixed white pill as the category badge above.
                    color: COLORS.purple.DEFAULT,
                    fontFamily: FONTS.bodyBold,
                  }}
                >
                  {t('vehicle_detail.booking_coming_soon')}
                </Text>
              </View>
            )}
            {vehicle.isInstantBook && isBookable && (
              <View
                style={{
                  backgroundColor: 'rgba(255,255,255,0.92)',
                  paddingHorizontal: SPACING.sm - 1,
                  paddingVertical: SPACING.xs,
                  borderRadius: RADIUS.sm,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Zap size={9} color={COLORS.amber.dark} fill={COLORS.amber.dark} />
                <Text
                  style={{
                    fontSize: 12,
                    color: COLORS.amber.dark,
                    fontFamily: FONTS.body,
                  }}
                >
                  {t('explore.instant_book')}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Content */}
        <View style={{ paddingTop: SPACING.sm + 2, paddingHorizontal: 2 }}>
          {/* Title and price share one line only while both fit it. Past the
              baseline text size the price alone wraps onto two lines inside its
              45% column, so the pair stacks instead: title on its own measure,
              price under it, neither truncated. */}
          <View
            style={{
              flexDirection: enlarged ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <View
              style={{ flex: 1, alignSelf: 'stretch', paddingRight: enlarged ? 0 : SPACING.sm }}
            >
              <Text
                style={{
                  fontSize: titleSize,
                  color: colors.textPrimary,
                  fontFamily: FONTS.bodyBold,
                  letterSpacing: -0.2,
                }}
                // "Toyota Corolla" is 14pt on a 230dp card; at 2x it needs two
                // lines, and one line meant "Toyota Cor…" — the model name, which
                // is the whole point of the card, cut off.
                numberOfLines={enlarged ? 2 : 1}
              >
                {vehicle.makeName} {vehicle.modelName}
              </Text>
              {/* A listing with no reviews is not a zero-star listing. Rendering
                  five empty stars next to "0.0 (0)" reads as a terrible car, and
                  at launch almost every car is new — so the whole grid read that
                  way. Marketplaces label this state instead of scoring it. */}
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: SPACING.xs,
                  marginTop: 3,
                }}
              >
                {(vehicle.ratingCount ?? 0) > 0 ? (
                  <>
                    <Rating value={vehicle.ratingAvg} size={starSize} />
                    <Text
                      style={{
                        fontSize: starSize,
                        color: colors.textPrimary,
                        fontFamily: FONTS.body,
                        fontVariant: ['tabular-nums'],
                      }}
                    >
                      {(vehicle.ratingAvg ?? 0).toFixed(1)}
                    </Text>
                  </>
                ) : (
                  <Text
                    style={{
                      fontSize: starSize,
                      color: colors.textBrand,
                      fontFamily: FONTS.bodyBold,
                    }}
                  >
                    {t('explore.rating_new')}
                  </Text>
                )}
                <Text
                  style={{
                    fontSize: metaSize,
                    color: colors.textSecondary,
                    fontFamily: FONTS.body,
                    fontVariant: ['tabular-nums'],
                    flexShrink: 1,
                  }}
                  numberOfLines={1}
                  adjustsFontSizeToFit
                  minimumFontScale={0.82}
                >
                  {(vehicle.ratingCount ?? 0) > 0 ? `(${vehicle.ratingCount})` : ''}
                  {vehicle.distanceKm != null && vehicle.distanceKm >= 0.1
                    ? `${(vehicle.ratingCount ?? 0) > 0 ? ' · ' : ''}${vehicle.distanceKm.toFixed(1)}km`
                    : ''}
                </Text>
              </View>
            </View>
            <View
              style={{
                alignItems: enlarged ? 'flex-start' : 'flex-end',
                maxWidth: enlarged ? '100%' : '45%',
                marginTop: enlarged ? SPACING.xs : 0,
              }}
            >
              <Text
                numberOfLines={1}
                adjustsFontSizeToFit={!enlarged}
                minimumFontScale={enlarged ? undefined : 0.8}
                style={{
                  fontSize: priceSize,
                  color: colors.textPrimary,
                  fontFamily: FONTS.bodyBold,
                  letterSpacing: -0.3,
                  fontVariant: ['tabular-nums'],
                }}
              >
                {price}
              </Text>
              <Text
                style={{ fontSize: 12, color: colors.textSecondary, fontFamily: FONTS.body }}
                numberOfLines={1}
              >
                {rateLabel}
              </Text>
            </View>
          </View>
        </View>
      </Pressable>
      <Pressable
        onPress={handleFavoriteToggle}
        accessibilityRole="button"
        accessibilityLabel={
          isFavorite ? t('a11y.remove_from_favorites') : t('a11y.add_to_favorites')
        }
        accessibilityState={{ selected: isFavorite }}
        testID="explore.vehicle-card-favorite"
        style={{
          position: 'absolute',
          // Same inset as the badge cluster on the opposite corner
          // (SPACING.sm + 2). At SPACING.xs the heart crowded the corner and
          // its top edge sat 6pt above the "Sedán" badge it shares the row
          // with.
          top: SPACING.sm + 2,
          right: SPACING.sm + 2,
          width: 44,
          height: 44,
          borderRadius: 22,
          backgroundColor: 'rgba(255,255,255,0.88)',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Animated.View style={heartAnimatedStyle}>
          <Heart
            size={18}
            color={isFavorite ? COLORS.coral.DEFAULT : 'rgba(0,0,0,0.25)'}
            fill={isFavorite ? COLORS.coral.DEFAULT : 'none'}
            strokeWidth={2}
          />
        </Animated.View>
      </Pressable>
    </View>
  )
}

function VehicleCardSkeleton({
  desktop,
  mobileWidth,
}: {
  desktop?: boolean
  mobileWidth?: number
}) {
  const resolvedMobileWidth = mobileWidth ?? MOBILE_WIDTH
  // Must match the real card's media frame exactly, or the grid visibly
  // reflows the moment the data lands.
  const imgH = desktop ? 220 : Math.round(resolvedMobileWidth / MOBILE_IMAGE_ASPECT_RATIO)

  return (
    <View style={desktop ? { flex: 1 } : { width: resolvedMobileWidth }}>
      <Skeleton width="100%" height={imgH} borderRadius={RADIUS.lg} />
      <View style={{ paddingTop: SPACING.sm + 2 }}>
        <Skeleton.Text lines={2} lastLineWidth="60%" />
      </View>
    </View>
  )
}

// React.memo on the base prevents the entire grid (~50 cards on /explore)
// from re-rendering when ANY single favorite toggles, when search filters
// don't affect a card's data, or when the parent re-renders for unrelated
// reasons. Props are primitives + stable callbacks (parent useCallback) so
// the default shallow compare is correct here.
const MemoizedVehicleCard = memo(VehicleCardBase)
MemoizedVehicleCard.displayName = 'VehicleCard'

export const VehicleCard = Object.assign(MemoizedVehicleCard, {
  Skeleton: VehicleCardSkeleton,
})
